import { useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import BlogGeneration from './components/BlogGeneration';
import { BlogGenerationProvider } from './Providers/BGProviders';
import { useQuery } from '@tanstack/react-query';
import getBlogBlueprints from './api/get-blog-blueprints';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';

import BGNoDataFound from './components/BGNoDataFound';
import { useGetAgentsStatus } from '../hooks/useGetAgentsStatus';
import { useOrbitAISSE } from '../hooks/useOrbitAISSE';
import OrbitAIGuard from '../Components/OrbitAIGuard';
import UpgradeToOrbitAI from '../Components/UpgradeToOrbitAI';
import BlogGenerationBreadcrumbs from './components/BlogGenerationBreadcrumbs';
import BlogGenerationHeader from './components/BlogGenerationHeader';
import OrbitAgentSiteGuard from '../Components/OrbitAgentSiteGuard';
import { keywordResearchSubmenuTitles } from 'src/common/config/TextMessage';
import { Card } from 'src/components/UI/Card';

function BlogGenerationFallback() {
  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <BlogGenerationBreadcrumbs />
        <BlogGenerationHeader />
      </div>
       <Card>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">Blog Generation</p>
          <div className="w-[600px] mx-auto my-10">
            <UpgradeToOrbitAI />
          </div>
        </div>
      </Card>
    </div>
  );
}

function BlogGenerationContent() {
  // get site id from redux
  const { _id: site_id, user_id } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  );
  const siteSoftDeleted = useSelector(
    (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
  );

  useOrbitAISSE(site_id);

  const {
    data: blogBlueprints,
    isLoading,
    isPending,
    refetch: refetchBlogs
  } = useQuery({
    ...getBlogBlueprints(user_id, site_id),
    // Poll every 10s while any blueprint is still generating, so spinner
    // resolves automatically after a page refresh.
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasProcessing = data?.runs?.some((run) =>
        run.blueprints?.some((bp) => bp.generation_status === 'processing')
      );
      return hasProcessing ? 10_000 : false;
    }
  });

  const { data: agentsStatus } = useGetAgentsStatus(site_id);

  const isBlogAgentProcessing = agentsStatus?.agents?.['blog-generation']?.status === 'processing';

  useEffect(() => {
    if (!isBlogAgentProcessing && agentsStatus) {
      refetchBlogs();
    }
  }, [isBlogAgentProcessing, agentsStatus, refetchBlogs]);

  const hasNoData = useMemo(() => {
    if (isBlogAgentProcessing) return false;
    // return true if blogBlueprints is null or empty or blogBlueprints.data is empty
    return (
      blogBlueprints &&
      blogBlueprints.status &&
      blogBlueprints.runs.length === 0
    );
  }, [blogBlueprints, isBlogAgentProcessing]);

  if (hasNoData) {
    return (
      <div
        className="-mt-8 pt-8"
        style={{
          minHeight: 'calc(100vh - 51px)',
          backgroundColor: 'var(--color-bg-cream)'
        }}
      >
        <BGNoDataFound siteSoftDeleted={siteSoftDeleted} />
      </div>
    );
  }

  return (
    <BlogGeneration
      blogBlueprints={blogBlueprints}
      isLoading={isLoading || isBlogAgentProcessing}
      isPending={isPending}
      siteSoftDeleted={siteSoftDeleted}
    />
  );
}

function BlogGenerationPage() {
  return (
    <BlogGenerationProvider>
      <Helmet>
        <title>SEOSpace | Blog Generation</title>
      </Helmet>

      <OrbitAIGuard fallback={<BlogGenerationFallback />}>
        <OrbitAgentSiteGuard
          title={keywordResearchSubmenuTitles.blogPostAgent}
          headerComponent={<BlogGenerationHeader />}
        >
          <BlogGenerationContent />
        </OrbitAgentSiteGuard>
      </OrbitAIGuard>
    </BlogGenerationProvider>
  );
}

export default BlogGenerationPage;
